import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  type ElementRef,
  input,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import { type ControlValueAccessor } from '@angular/forms';

import { NgpToggleGroup, NgpToggleGroupItem } from 'ng-primitives/toggle-group';
import { provideValueAccessor } from 'ng-primitives/utils';
import { twMerge } from 'tailwind-merge';

import { I18nPipe } from '@core/i18n';

import { IconComponent } from '@shared/icon';

import { groupVariants, itemVariants, slidingRectVariants } from './toggle-group.cva';
import { type SlidingRect, type ToggleOption, type ToggleOrientation } from './toggle.types';

@Component({
  selector: 'ml-toggle-group',
  imports: [NgpToggleGroup, NgpToggleGroupItem, IconComponent, I18nPipe],
  providers: [provideValueAccessor(ToggleGroupComponent)],
  host: { '[class]': 'hostClass()' },
  templateUrl: './toggle-group.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleGroupComponent implements ControlValueAccessor {
  readonly options = input.required<readonly ToggleOption[]>();
  readonly currentValue = input<string | null>(null);
  readonly orientation = input<ToggleOrientation>('horizontal');
  readonly isReadonly = input(false, { transform: booleanAttribute });
  readonly extraClass = input<string>('', { alias: 'class' });

  private readonly itemRefs = viewChildren<ElementRef<HTMLButtonElement>>('item');

  private onChange: (value: string | null) => void = () => {
    return;
  };
  private onTouched: () => void = () => {
    return;
  };
  private hasRegisteredOnChange = false;
  private lastEmittedValue: string | null = null;

  protected readonly value = signal<string | null>(null);
  protected readonly disabled = signal(false);
  protected readonly rect = signal<SlidingRect | null>(null);

  protected readonly slidingRectClass = slidingRectVariants;

  protected readonly selectedValue = computed(() =>
    this.normalizeValue(this.value() ?? this.currentValue() ?? null),
  );

  protected readonly selectedValues = computed(() => {
    const value = this.selectedValue();
    return value ? [value] : [];
  });
  protected readonly hostClass = computed(() => twMerge('inline-flex', this.extraClass()));
  protected readonly groupClass = computed(() =>
    groupVariants({
      orientation: this.orientation(),
      readonly: this.isReadonly() && !this.disabled(),
    }),
  );
  protected readonly itemClass = computed(() => {
    const hasLabel = this.options().some((opt) => opt?.label);
    return itemVariants({ onlyIcon: !hasLabel });
  });

  constructor() {
    effect(() => {
      const selectedValue = this.selectedValue();
      const refs = this.itemRefs();

      if (!refs.length) return;

      untracked(() => this.updateRect(selectedValue, refs));
    });

    effect((onCleanup) => {
      const refs = this.itemRefs();

      if (!refs.length) return;

      const observer = new ResizeObserver(() => {
        untracked(() => this.updateRect(this.selectedValue(), this.itemRefs()));
      });

      for (const ref of refs) {
        observer.observe(ref.nativeElement);
      }

      onCleanup(() => observer.disconnect());
    });

    effect(() => {
      const selectedValue = this.selectedValue();
      const currentValue = this.value();

      if (selectedValue === null || selectedValue === currentValue) return;

      untracked(() => this.setValueAndEmit(selectedValue));
    });

    afterNextRender(() => {
      this.updateRect(this.selectedValue(), this.itemRefs());
    });
  }

  writeValue(value: string | null): void {
    this.value.set(value);
    this.lastEmittedValue = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
    this.hasRegisteredOnChange = true;

    const selectedValue = this.selectedValue();

    if (selectedValue !== null && selectedValue !== this.value()) {
      this.setValueAndEmit(selectedValue);
    }
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(value: boolean): void {
    this.disabled.set(value);
  }

  protected onValueChange(value: string[]): void {
    const nextValue = this.normalizeValue(value[0] ?? this.selectedValue());

    if (nextValue === null) return;

    this.setValueAndEmit(nextValue);
    this.onTouched();
  }

  private getDefaultValue(): string | null {
    return this.options()[0]?.value ?? null;
  }

  private hasOption(value: string): boolean {
    return this.options().some((option) => option.value === value);
  }

  private normalizeValue(
    value: string | null,
    defaultValue = this.getDefaultValue(),
  ): string | null {
    if (value === null) return defaultValue;

    return this.hasOption(value) || defaultValue === null ? value : defaultValue;
  }

  private updateRect(value: string | null, refs: readonly ElementRef<HTMLButtonElement>[]): void {
    const selectedValue = value;

    if (selectedValue === null) {
      this.rect.set(null);

      return;
    }

    const idx = this.options().findIndex((o) => o.value === selectedValue);

    if (idx === -1 || refs[idx] === undefined) {
      this.rect.set(null);

      return;
    }

    requestAnimationFrame(() => {
      const itemEl = refs[idx]?.nativeElement;

      if (!itemEl) return;

      const next = this.slidingRectFromElement(itemEl);

      if (!next) return;

      this.rect.set(next);
    });
  }

  private slidingRectFromElement(el: HTMLElement): SlidingRect | null {
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    if (!width || !height) return null;

    return {
      top: el.offsetTop,
      left: el.offsetLeft,
      width,
      height,
    };
  }

  private setValueAndEmit(value: string): void {
    this.value.set(value);
    this.emitValue(value);
  }

  private emitValue(value: string): void {
    if (!this.hasRegisteredOnChange || this.lastEmittedValue === value) return;

    this.onChange(value);
    this.lastEmittedValue = value;
  }
}
